import { z } from 'zod';

export enum AICousticsEnhancement_QualityTier {
  QUALITY_TIER_UNSPECIFIED = 0,
  QUALITY_TIER_STANDARD = 1,
  QUALITY_TIER_HIGH = 2,
  QUALITY_TIER_FAST = 3,
  UNRECOGNIZED = -1,
}

export const AiCousticsSchema = z
  .object({
    enabled: z.boolean(),
    modelQualityTier: z
      .nativeEnum(AICousticsEnhancement_QualityTier)
      .optional(),
    noiseReduction: z.number().optional(),
    voiceGain: z
      .number()
      .optional()
      .describe('Amplification multiplier for voice signal (1.0 = no change)'),
    noiseGate: z
      .boolean()
      .optional()
      .describe('Whether to suppress audio below a noise threshold'),
  })
  .describe('ai-coustics audio enhancement configuration');

export type AiCoustics = z.infer<typeof AiCousticsSchema>;

export const AudioEnhancementSchema = z
  .object({
    provider: z
      .discriminatedUnion('$case', [
        z.object({ $case: z.literal('aiCoustics'), value: AiCousticsSchema }),
      ])
      .optional(),
  })
  .describe('Audio enhancement provider configuration for a voice channel');

export type AudioEnhancement = z.infer<typeof AudioEnhancementSchema>;

export const AsrConfigSchema = z
  .object({
    providerId: z.string().describe('ASR provider identifier'),
    modelId: z.string().describe('ASR model identifier'),
    language: z.string().optional(),
  })
  .describe('Automatic speech recognition provider and model configuration');

export type AsrConfig = z.infer<typeof AsrConfigSchema>;

export const FillerUtteranceSchema = z.object({
  message: z.string(),
});

export type FillerUtterance = z.infer<typeof FillerUtteranceSchema>;

export const SilenceFillerUtterancesSchema = z
  .object({
    enabled: z.boolean(),
    utterances: z
      .array(FillerUtteranceSchema)
      .describe('Messages played during silence (e.g., "Just a moment")'),
    initialIntervalSeconds: z
      .number()
      .int()
      .nonnegative()
      .describe('Seconds before the first filler utterance plays'),
    intervalSeconds: z
      .number()
      .int()
      .nonnegative()
      .describe('Seconds between subsequent filler utterances'),
    randomize: z.boolean().describe('Whether to randomize utterance order'),
  })
  .describe(
    'Filler utterances played during silence to prevent dead air while the agent processes',
  );

export type SilenceFillerUtterances = z.infer<
  typeof SilenceFillerUtterancesSchema
>;

export const VadConfigSchema = z
  .object({
    speechEndWindowSeconds: z
      .number()
      .nonnegative()
      .describe(
        'Seconds of silence after speech before the caller is considered done speaking',
      ),
  })
  .describe('Voice activity detection configuration');

export type VadConfig = z.infer<typeof VadConfigSchema>;

export const BargeInConfigSchema = z
  .object({
    enabled: z.boolean(),
    allowedFirstTurn: z.boolean().optional(),
    maxPerCall: z.number().int().nonnegative().optional(),
    minSpeechDurationSeconds: z.number().nonnegative().optional(),
  })
  .describe('Barge-in configuration for a voice channel');

export type BargeInConfig = z.infer<typeof BargeInConfigSchema>;
